/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// pages/dashboard/ApplyJobForm.tsx (with resume upload)
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useApplication } from "../../context/ApplicationContext";
import { useDarkMode } from "../../context/DarkModeContext";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  description: string;
  category: string;
  deadline?: string;
  createdAt: string;
  postedBy: {
    id: string;
    name: string;
    email: string;
  };
}

// interface Application {
//   id: string;
//   jobId: string;
//   userId: string;
//   coverLetter: string;
//   status: string;
//   appliedAt: string;
//   resumeUrl?: string;
// }

// Add missing function declarations

export default function ApplyJobForm() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAppliedToJob, submitApplication } = useApplication();
  const { isDark } = useDarkMode();

  const [job, setJob] = useState<Job | null>(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [resume, setResume] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/jobs/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch job details");
      }

      const data = await response.json();
      console.log("Job data:", data);
      setJob(data.job || data);
    } catch (err: any) {
      console.error("Error fetching job:", err);
      setError(err.message || "Failed to fetch job details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !job || !jobId) return;

    // Check if already applied using the context
    if (hasAppliedToJob(jobId)) {
      setError("You have already applied to this job");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // If resume file is uploaded, handle file upload first
      let resumeUrl = "";
      if (resume) {
        console.log("Uploading resume file...");
        resumeUrl = await uploadResume(resume);
        console.log("Resume uploaded, URL:", resumeUrl);
      }

      // Prepare application data
      const applicationData: {
        jobId: string;
        coverLetter: string;
        resumeUrl?: string;
      } = {
        jobId,
        coverLetter,
      };

      // Only include resumeUrl if it was uploaded
      if (resumeUrl) {
        applicationData.resumeUrl = resumeUrl;
      }

      console.log("Submitting application with data:", applicationData);

      // Use the submitApplication function from context
      const result = await submitApplication(applicationData);

      if (result.success) {
        setApplicationSuccess(true);
        // Clear form
        setCoverLetter("");
        setResume(null);
      } else {
        throw new Error(result.error || "Failed to submit application");
      }
    } catch (err: any) {
      console.error("Error submitting application:", err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadResume = async (file: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "upload_preset",
        import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
      );
      formData.append("resource_type", "raw");
      formData.append("folder", "resumes");

      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

      console.log("Uploading resume to Cloudinary...", {
        cloudName,
        uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
        fileName: file.name,
      });

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Upload failed: ${errorData.error?.message || "Unknown error"}`,
        );
      }

      const data = await response.json();
      console.log("Upload successful! File URL:", data.secure_url);
      return data.secure_url;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
      ];

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes

      if (!allowedTypes.includes(file.type)) {
        setError("Please upload a valid file type (PDF, DOC, DOCX, TXT)");
        setResume(null);
        e.target.value = ""; // Clear the file input
        return;
      }

      if (file.size > maxSize) {
        setError("File size must be less than 5MB");
        setResume(null);
        e.target.value = ""; // Clear the file input
        return;
      }

      setResume(file);
      setError(""); // Clear any previous errors
    } else {
      setResume(null);
    }
  };

  const handleViewApplications = () => {
    navigate("/dashboard/applications");
  };

  const handleBrowseJobs = () => {
    navigate("/dashboard/find-job");
  };

  const isJobExpired = job?.deadline && new Date(job.deadline) < new Date();
  const hasApplied = jobId ? hasAppliedToJob(jobId) : false;

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={isDark ? "text-gray-300" : "text-gray-600"}>
            Loading job details...
          </p>
        </div>
      </div>
    );
  }

  if (!job && !loading) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className="text-center">
          <h2
            className={`text-2xl font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Job Not Found
          </h2>
          <p className={`mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            The job you're looking for doesn't exist or may have been removed.
          </p>
          <button
            onClick={() => navigate("/dashboard/find-job")}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} py-8`}
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Message Banner */}
        {applicationSuccess && (
          <div
            className={`border rounded-lg p-6 mb-6 shadow-sm ${
              isDark
                ? "bg-green-900/20 border-green-700 text-green-300"
                : "bg-green-50 border-green-200 text-green-800"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-full mr-4 ${
                    isDark ? "bg-green-800/50" : "bg-green-100"
                  }`}
                >
                  <svg
                    className={`w-6 h-6 ${isDark ? "text-green-400" : "text-green-600"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h3
                    className={`text-lg font-semibold mb-1 ${
                      isDark ? "text-green-300" : "text-green-800"
                    }`}
                  >
                    Application Submitted Successfully!
                  </h3>
                  <p className={isDark ? "text-green-400" : "text-green-600"}>
                    Your application for <strong>{job?.title}</strong> at{" "}
                    <strong>{job?.company}</strong> has been submitted. The
                    employer will review your application and contact you if
                    you're shortlisted.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleViewApplications}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                View My Applications
              </button>
              <button
                onClick={handleBrowseJobs}
                className={`px-4 py-2 border rounded-lg hover:bg-green-50 transition-colors text-sm font-medium ${
                  isDark
                    ? "border-green-500 text-green-400 hover:bg-green-900/30"
                    : "border-green-600 text-green-600 hover:bg-green-50"
                }`}
              >
                Browse More Jobs
              </button>
            </div>
          </div>
        )}

        <div
          className={`rounded-lg shadow-md overflow-hidden ${
            isDark ? "bg-gray-800" : "bg-white"
          }`}
        >
          {/* Job Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
            <h1 className="text-3xl font-bold mb-2">Apply for {job?.title}</h1>
            <p className="text-xl opacity-90 mb-2">
              {job?.company} • {job?.location}
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                {job?.type}
              </span>
              <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                {job?.category}
              </span>
              {job?.salary && (
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                  {job.salary}
                </span>
              )}
            </div>
          </div>

          {/* Job Details and Form */}
          <div className="p-6">
            {/* Already Applied Warning */}
            {hasApplied && !applicationSuccess && (
              <div
                className={`border px-4 py-3 rounded mb-6 ${
                  isDark
                    ? "bg-yellow-900/20 border-yellow-700 text-yellow-300"
                    : "bg-yellow-100 border-yellow-400 text-yellow-700"
                }`}
              >
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <strong>Already Applied:</strong> You have already submitted
                  an application for this position.
                </div>
                <button
                  onClick={handleViewApplications}
                  className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm"
                >
                  View My Applications
                </button>
              </div>
            )}

            {/* Deadline Warning */}
            {isJobExpired && (
              <div
                className={`border px-4 py-3 rounded mb-6 ${
                  isDark
                    ? "bg-red-900/20 border-red-700 text-red-300"
                    : "bg-red-100 border-red-400 text-red-700"
                }`}
              >
                <strong>Application Closed:</strong> This job posting has
                expired. Applications are no longer being accepted.
              </div>
            )}

            {job?.deadline && !isJobExpired && !hasApplied && (
              <div
                className={`border px-4 py-3 rounded mb-6 ${
                  isDark
                    ? "bg-blue-900/20 border-blue-700 text-blue-300"
                    : "bg-blue-100 border-blue-400 text-blue-700"
                }`}
              >
                <strong>Application Deadline:</strong>{" "}
                {new Date(job.deadline).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            )}

            {/* Job Description */}
            <div className="mb-6">
              <h3
                className={`text-lg font-semibold mb-3 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Job Description
              </h3>
              <div className="prose max-w-none">
                <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                  {job?.description}
                </p>
              </div>
            </div>

            {/* Application Form - Only show if not already applied and not expired */}
            {!hasApplied && !isJobExpired && !applicationSuccess && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="coverLetter"
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Cover Letter *
                  </label>
                  <textarea
                    id="coverLetter"
                    value={coverLetter}
                    onChange={(e) => setCoverLetter(e.target.value)}
                    required
                    rows={8}
                    minLength={50}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300 text-gray-900"
                    }`}
                    placeholder="Write your cover letter here. Explain why you're a good fit for this position, highlight your relevant experience, and express your enthusiasm for the role..."
                  />
                  <p
                    className={`text-sm mt-1 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Minimum 50 characters. Be specific about how your skills
                    match the job requirements. {coverLetter.length}/50
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="resume"
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Resume (Optional)
                  </label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleResumeChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1"
                        : "border-gray-300 text-gray-900"
                    }`}
                  />
                  <p
                    className={`text-sm mt-1 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Accepted formats: PDF, DOC, DOCX, TXT (Max: 5MB). If you
                    don't upload a resume, we'll use your profile information.
                  </p>
                  {resume && (
                    <p
                      className={`text-sm mt-1 ${
                        isDark ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      ✓ Resume selected: {resume.name} (
                      {(resume.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>

                {error && (
                  <div
                    className={`border px-4 py-3 rounded ${
                      isDark
                        ? "bg-red-900/20 border-red-700 text-red-300"
                        : "bg-red-100 border-red-400 text-red-700"
                    }`}
                  >
                    <strong>Error:</strong> {error}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/find-job")}
                    className={`px-6 py-3 border rounded-md transition-colors font-medium ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !user || coverLetter.length < 50}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </span>
                    ) : (
                      "Submit Application"
                    )}
                  </button>
                </div>

                {!user && (
                  <div
                    className={`border px-4 py-3 rounded ${
                      isDark
                        ? "bg-yellow-900/20 border-yellow-700 text-yellow-300"
                        : "bg-yellow-100 border-yellow-400 text-yellow-700"
                    }`}
                  >
                    Please log in to submit your application.
                  </div>
                )}
              </form>
            )}

            {/* Show alternative actions if already applied or expired */}
            {(hasApplied || isJobExpired) && !applicationSuccess && (
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => navigate("/dashboard/find-job")}
                  className={`px-6 py-3 border rounded-md transition-colors font-medium ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Browse More Jobs
                </button>
                <button
                  onClick={handleViewApplications}
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium"
                >
                  View My Applications
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Application Tips - Only show when form is visible */}
        {!hasApplied && !isJobExpired && !applicationSuccess && (
          <div
            className={`mt-8 rounded-lg p-6 ${
              isDark
                ? "bg-blue-900/20 border border-blue-800/50 text-blue-300"
                : "bg-blue-50 text-blue-800"
            }`}
          >
            <h3 className="text-lg font-semibold mb-3">Application Tips</h3>
            <ul className="space-y-2 text-sm">
              <li>
                • Tailor your cover letter to this specific job and company
              </li>
              <li>• Highlight relevant experience and achievements</li>
              <li>• Explain why you're interested in this particular role</li>
              <li>• Keep your cover letter concise and focused</li>
              <li>• Proofread for spelling and grammar errors</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
